import type {
  MemoryEstimate,
  SolverConfig,
  SolverError,
  SolverProgress,
  SolverResult,
  SolverWorkerRequest,
  SolverWorkerResponse,
} from '../types/solver'

type Listener = (response: SolverWorkerResponse) => void

export class SolverClient {
  private worker: Worker | null = null
  private listeners = new Set<Listener>()
  private cancelCurrent: (() => void) | null = null

  private createWorker(): Worker {
    this.worker?.terminate()
    const worker = new Worker(new URL('./solver.worker.ts', import.meta.url), { type: 'module' })
    worker.addEventListener('message', (event: MessageEvent<SolverWorkerResponse>) => {
      this.listeners.forEach((listener) => listener(event.data))
    })
    worker.addEventListener('error', (event) => {
      const error: SolverError = {
        code: 'WORKER_STOPPED',
        message: '計算Workerが予期せず停止しました。',
        detail: event.message,
      }
      this.listeners.forEach((listener) => listener({ type: 'error', payload: error }))
    })
    this.worker = worker
    return worker
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  initialize(): Promise<void> {
    const worker = this.createWorker()
    return this.waitFor<void>(worker, { type: 'initialize' }, (response) => response.type === 'ready' ? undefined : null)
  }

  estimate(config: SolverConfig): Promise<MemoryEstimate> {
    const worker = this.worker ?? this.createWorker()
    return this.waitFor(worker, { type: 'estimate', payload: config }, (response) => response.type === 'estimate' ? response.payload : null)
  }

  solve(config: SolverConfig, onProgress: (progress: SolverProgress) => void, onEstimate?: (estimate: MemoryEstimate) => void): Promise<SolverResult> {
    const worker = this.createWorker()
    return new Promise((resolve, reject) => {
      const finish = () => {
        cleanup()
        this.cancelCurrent = null
      }
      const handler = (event: MessageEvent<SolverWorkerResponse>) => {
        const response = event.data
        if (response.type === 'progress') onProgress(response.payload)
        if (response.type === 'estimate') onEstimate?.(response.payload)
        if (response.type === 'result') {
          finish()
          resolve(response.payload)
        }
        if (response.type === 'cancelled') {
          finish()
          reject(new DOMException('計算を中断しました', 'AbortError'))
        }
        if (response.type === 'error') {
          finish()
          reject(Object.assign(new Error(response.payload.message), { solverError: response.payload }))
        }
      }
      const cleanup = () => worker.removeEventListener('message', handler)
      this.cancelCurrent = () => {
        finish()
        reject(new DOMException('計算を中断しました', 'AbortError'))
      }
      worker.addEventListener('message', handler)
      worker.postMessage({ type: 'solve', payload: config } satisfies SolverWorkerRequest)
    })
  }

  cancel(): void {
    if (!this.worker) return
    this.worker.postMessage({ type: 'cancel' } satisfies SolverWorkerRequest)
    this.cancelCurrent?.()
    this.worker.terminate()
    this.worker = null
  }

  terminate(): void {
    this.cancelCurrent?.()
    this.worker?.terminate()
    this.worker = null
  }

  private waitFor<T>(worker: Worker, request: SolverWorkerRequest, pick: (response: SolverWorkerResponse) => T | null | undefined): Promise<T> {
    return new Promise((resolve, reject) => {
      const handler = (event: MessageEvent<SolverWorkerResponse>) => {
        const response = event.data
        if (response.type === 'error') {
          worker.removeEventListener('message', handler)
          reject(Object.assign(new Error(response.payload.message), { solverError: response.payload }))
          return
        }
        const value = pick(response)
        if (value !== null) {
          worker.removeEventListener('message', handler)
          resolve(value as T)
        }
      }
      worker.addEventListener('message', handler)
      worker.postMessage(request)
    })
  }
}

export const solverClient = new SolverClient()
